import deepmerge from '../utils/deepmerge.js';
/**
 * @typedef {Object} Language
 * @property {string} name
 * @property {string} short
 */
/**
 * name and short is mandatory, but it can contain additional properties
 * https://type-level-typescript.com/objects-and-records
 *
 * @typedef {Object} Translate
 * @property {string} name
 * @property {string} short
 */
export default class Localization {
    constructor() {
        /** @type {Translate} */
        this.localization = {};
        /** @type {string} */
        this.lang = '';
        /** @type {Array<Language>} */
        this.availableLanguages = [];
        /** @type {string} */
        this.host = '';
        /** @type {boolean} */
        this.loaded = false;
        /** @type {string} */
        this.version = new Date().toLocaleDateString();
        /** @type {string} */
        this.overwriteHost = '';
    }
    /**
     * Inits the Localization object
     *
     * @param {Array<Language>} languages
     * @param {string} host This URL contains the translate
     * @param {Translate?} [defaultLocalization]
     * @param {string} [overwriteHost] - If given, when fetching localization it will merge the two localizations
     *
     */
    init(languages, host, defaultLocalization = null, overwriteHost = '') {
        this.availableLanguages = languages;
        this.host = host;
        this.overwriteHost = overwriteHost;
        if (defaultLocalization) {
            this.lang = 'en-EN';
            this.setTranslation(defaultLocalization);
        }
        return this;
    }
    /**
     * Load the localization, if needed fetch the json file
     * If the language is not supported, it defaults to english
     *
     * @param {string} locale
     */
    async load(locale) {
        const lang = locale.length === 5 ? locale : `${locale}-${locale.toUpperCase()}`;
        if (lang === this.lang) {
            return this.lang;
        }
        const isSupportedLang = this.availableLanguages.some(_lang => _lang.short === lang);
        if (isSupportedLang) {
            // fully equal like en-EN and en-EN
            this.lang = lang;
        }
        else {
            // similar like en-EN and en-GB
            const similarLang = this.availableLanguages.find(_lang => _lang.short.slice(0, 2) === lang.slice(0, 2));
            const backupLang = similarLang?.short || 'en-EN';
            if (backupLang === this.lang) {
                return this.lang;
            }
            this.lang = backupLang;
        }
        this.setTranslation(await this.fetchLocalization(this.lang));
        return this.lang;
    }
    /**
     * Returns the translated message regarding to the params
     *
     * @param {string} page
     * @param {string} key
     * @param  {Array<string>} interpolations
     */
    get(page, key, ...interpolations) {
        const translatedText = this.getText(page, key, ...interpolations);
        return translatedText ?? `${page}.${key}`;
    }
    /**
     * Returns the translated message or the default value when the key is missing.
     *
     * @param {string} page
     * @param {string} key
     * @param {string} defaultValue
     * @param  {Array<string>} interpolations
     */
    getOrDefault(page, key, defaultValue, ...interpolations) {
        const translatedText = this.getText(page, key, ...interpolations);
        return translatedText ?? defaultValue;
    }
    /**
     *
     * @param {string} page
     * @param {string} key
     * @param  {Array<string>} interpolations
     */
    getText(page, key, ...interpolations) {
        const translations = this.localization[page];
        if (translations) {
            const translatedText = translations[key];
            if (translatedText) {
                return Localization.interpolated(translatedText, interpolations);
            }
        }
        return null;
    }
    /**
     * It set the this.localization to the given parameter and dispatch the 'languageChanged' CustomEvent
     * @param {Translate} translation
     */
    setTranslation(translation) {
        this.localization = translation;
        this.loaded = true;
        window.dispatchEvent(new CustomEvent('languageChanged', { detail: this.lang }));
    }
    /**
     * Gets file from the host
     *
     * @private
     * @param {String} language
     * @param {String} [companyId]
     */
    async fetchLocalization(language) {
        if (this.overwriteHost) {
            return this.fetchLocalizations(language);
        }
        const response = await fetch(`${this.host}/${language}.json?version=${this.version}`);
        if (response.ok) {
            return response.json();
        }
        throw new Error(`Failed to load localization: (${response.status}) ${response.statusText}`);
    }
    async fetchLocalizations(language) {
        const [original, overwrite] = await Promise.all([
            fetch(`${this.host}/${language}.json?version=${this.version}`),
            fetch(`${this.overwriteHost}/${language}.json?version=${this.version}`),
        ]);
        if (original.ok && overwrite.ok) {
            return deepmerge(await original.json(), await overwrite.json());
        }
        else if (!original.ok) {
            throw new Error(`Failed to load original localization: (${original.status}) ${original.statusText}`);
        }
        else {
            throw new Error(`Failed to load overwrite localization: (${overwrite.status}) ${overwrite.statusText}`);
        }
    }
    /**
     * Replaces {i} with given string from the array
     *
     * @private
     * @param {string} text
     * @param {Array<string>} interpolations
     */
    static interpolated(text, interpolations) {
        let result = text;
        interpolations.forEach((value, i) => {
            result = result.replace(`{${i}}`, value);
        });
        return result;
    }
    /**
     * Factory method to return the instance of the object, if need call the constructor
     * @returns {Localization}
     */
    static getInstance() {
        if (!Localization.instance) {
            Localization.instance = new Localization();
        }
        return Localization.instance;
    }
}
/** @type {Localization} */
Localization.instance = null;
